import { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2, LogOut, Users, ShoppingBag, AlertTriangle, Megaphone, Trash2, Server, Shield, Percent, Save, FileCheck, Eye, Check, X, UtensilsCrossed } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import AdminOrdersBoard from "@/components/AdminOrdersBoard";
import AdminProviders from "@/components/AdminProviders";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      onLogin();
    } catch {
      toast.error("Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F5F3FA" }}>
      <div className="w-full max-w-sm mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: "#2D2766" }}>
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-sm text-gray-500 mt-1">Elderra Agent Dashboard</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 text-base focus:outline-none focus:ring-2 focus:ring-[#2D2766]"
              data-testid="admin-password-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-3 rounded-xl text-white font-semibold text-base transition-all disabled:opacity-50"
              style={{ backgroundColor: "#2D2766" }}
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ stats, svcLabel, statusColor }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-overview">
      <StatCard icon={Users} label="Total Users" value={stats.total_users} color="bg-purple-100 text-purple-600" />
      <StatCard icon={Users} label="Vendors" value={stats.total_vendors} color="bg-blue-100 text-blue-600" />
      <StatCard icon={ShoppingBag} label="Total Orders" value={stats.total_orders} color="bg-green-100 text-green-600" />
      <StatCard icon={AlertTriangle} label="SOS Alerts" value={stats.total_sos} color="bg-red-100 text-red-600" />
      <StatCard icon={ShoppingBag} label="Today Orders" value={stats.today_orders} color="bg-amber-100 text-amber-600" />
      <StatCard icon={Users} label="Today Sessions" value={stats.today_sessions} color="bg-teal-100 text-teal-600" />

      <div className="col-span-2 md:col-span-3 bg-white rounded-xl p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-900 mb-3 text-sm">Orders by Service</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.by_service || {}).map(([svc, count]) => (
            <Badge key={svc} className="bg-[#EDE8F8] text-[#2D2766] border-0 text-sm py-1 px-3">
              {svcLabel(svc)}: {count}
            </Badge>
          ))}
        </div>
      </div>

      <div className="col-span-2 md:col-span-3 bg-white rounded-xl p-4 shadow-sm border">
        <h3 className="font-semibold text-gray-900 mb-3 text-sm">Orders by Status</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.by_status || {}).map(([st, count]) => (
            <Badge key={st} className={`${statusColor(st)} border-0 text-sm py-1 px-3`}>
              {st.replace("_", " ")}: {count}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

function VendorsTab({ vendors, loading }) {
  return (
    <div className="space-y-3" data-testid="admin-vendors-list">
      <h2 className="font-semibold text-gray-900 text-lg">All Vendors</h2>
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-gray-400" /> :
        vendors.length === 0 ? <p className="text-gray-400 text-center py-8">No vendors yet</p> :
        vendors.map(v => (
          <div key={v.id} className="bg-white rounded-xl p-4 border shadow-sm" data-testid={`admin-vendor-${v.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm text-gray-900">{v.business_name}</p>
                <p className="text-xs text-gray-500">{v.name} &middot; {v.email}</p>
              </div>
              <Badge className={v.is_active ? "bg-green-100 text-green-700 border-0" : "bg-red-100 text-red-700 border-0"}>
                {v.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(v.service_categories || []).map(c => (
                <Badge key={c} className="bg-gray-100 text-gray-600 border-0 text-[10px]">{c.replace("_", " ")}</Badge>
              ))}
            </div>
          </div>
        ))
      }
    </div>
  );
}

function UsersTab({ users, loading, onSearch }) {
  const [q, setQ] = useState("");
  return (
    <div className="space-y-3" data-testid="admin-users-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-semibold text-gray-900 text-lg">All Users ({users.length})</h2>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearch?.(q)}
            placeholder="Search name, phone, city…"
            className="border rounded-lg px-3 py-1.5 text-sm w-56"
            data-testid="admin-user-search"
          />
          <button
            onClick={() => onSearch?.(q)}
            className="bg-[#2D2766] text-white text-sm px-3 py-1.5 rounded-lg"
            data-testid="admin-user-search-btn"
          >Search</button>
        </div>
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-gray-400" /> :
        users.length === 0 ? <p className="text-gray-400 text-center py-8">No users yet</p> :
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">City</th>
                <th className="px-3 py-2">Language</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2 text-right">Orders</th>
                <th className="px-3 py-2">Signed up</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.phone || Math.random()} className="border-b last:border-0 hover:bg-gray-50" data-testid={`admin-user-${u.phone}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {u.name || <span className="text-gray-400 italic">unknown</span>}
                    {u.age ? <span className="text-xs text-gray-400 ml-1">· {u.age}y</span> : null}
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-800">
                    {u.phone ? <a href={`https://wa.me/${u.phone}`} target="_blank" rel="noopener noreferrer" className="text-[#2D2766] hover:underline">+{u.phone}</a> : "—"}
                    {u.wa_verified ? <span className="ml-1 text-emerald-600 text-xs" title="WhatsApp verified">✓</span> : null}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {u.city || "—"}
                    {u.address && <div className="text-xs text-gray-400 max-w-xs truncate" title={u.address}>{u.address}</div>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{u.language || "—"}</td>
                  <td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{u.role || "user"}</Badge></td>
                  <td className="px-3 py-2 text-right text-gray-700">{u.order_count ?? 0}</td>
                  <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function SosTab({ sosAlerts, loading }) {
  return (
    <div className="space-y-3" data-testid="admin-sos-list">
      <h2 className="font-semibold text-gray-900 text-lg">SOS Alerts</h2>
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-gray-400" /> :
        sosAlerts.length === 0 ? <p className="text-gray-400 text-center py-8">No SOS alerts</p> :
        sosAlerts.map(a => (
          <div key={a.id} className="bg-white rounded-xl p-4 border border-red-200 shadow-sm" data-testid={`admin-sos-${a.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="font-medium text-sm text-red-800">{a.contact_name}</span>
                <Badge className="bg-red-100 text-red-700 border-0 text-xs">{a.contact_relation}</Badge>
              </div>
              <span className="text-xs text-gray-400">{new Date(a.triggered_at).toLocaleString()}</span>
            </div>
            <p className="text-xs text-gray-500">Phone: {a.contact_phone}</p>
            {a.lat && <a href={`https://www.google.com/maps?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View Location</a>}
          </div>
        ))
      }
    </div>
  );
}

function AnnouncementsTab({
  announcements, loading, newTitle, setNewTitle, newMessage, setNewMessage, newType, setNewType,
  postAnnouncement, deleteAnnouncement, sendSwiggyNudge, sendingSwiggyNudge,
}) {
  return (
    <div className="space-y-4" data-testid="admin-announcements">
      <h2 className="font-semibold text-gray-900 text-lg">Announcements & Alerts</h2>

      {/* Swiggy pre-link WhatsApp broadcast (one-time per user) */}
      <div className="bg-white rounded-xl p-4 border border-orange-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="swiggy-nudge-broadcast-card">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
            <UtensilsCrossed className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <p className="font-medium text-sm text-gray-900">Swiggy Pre-Link Nudge (WhatsApp)</p>
            <p className="text-xs text-gray-500 mt-0.5">Sends a one-time WhatsApp message to existing seniors inviting them to pre-link Swiggy. Each user only ever receives it once.</p>
          </div>
        </div>
        <button
          onClick={sendSwiggyNudge}
          disabled={sendingSwiggyNudge}
          className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 transition-colors whitespace-nowrap bg-orange-500 hover:bg-orange-600 w-full sm:w-auto"
          data-testid="send-swiggy-nudge-btn"
        >
          {sendingSwiggyNudge ? "Sending..." : "Send Broadcast"}
        </button>
      </div>

      {/* Create form */}
      <div className="bg-white rounded-xl p-4 border shadow-sm space-y-3" data-testid="announcement-form">
        <input
          type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Title" className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#2D2766]"
          data-testid="announcement-title-input"
        />
        <textarea
          value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Message..."  rows={3}
          className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#2D2766] resize-none"
          data-testid="announcement-message-input"
        />
        <div className="flex items-center gap-3">
          <select value={newType} onChange={(e) => setNewType(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm bg-white" data-testid="announcement-type-select">
            <option value="announcement">Announcement</option>
            <option value="fraud_alert">Fraud Alert</option>
            <option value="news">News</option>
          </select>
          <button onClick={postAnnouncement} disabled={!newTitle.trim() || !newMessage.trim()}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "#2D2766" }} data-testid="post-announcement-btn">
            Post
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto mt-4 text-gray-400" /> :
        announcements.map(a => (
          <div key={a.id} className={`bg-white rounded-xl p-4 border shadow-sm ${a.type === "fraud_alert" ? "border-red-200" : ""}`} data-testid={`announcement-${a.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Badge className={`border-0 text-xs ${a.type === "fraud_alert" ? "bg-red-100 text-red-700" : a.type === "news" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                  {a.type.replace("_", " ")}
                </Badge>
                <p className="font-medium text-sm text-gray-900">{a.title}</p>
              </div>
              <button onClick={() => deleteAnnouncement(a.id)} className="text-gray-400 hover:text-red-500" data-testid={`delete-announcement-${a.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">{a.message}</p>
            <p className="text-xs text-gray-400 mt-2">{new Date(a.created_at).toLocaleString()}</p>
          </div>
        ))
      }
    </div>
  );
}

function KycTab({ pendingKyc, loading, kycReviewingId, approveKyc, rejectKyc }) {
  return (
    <div className="space-y-4" data-testid="admin-kyc">
      <div>
        <h2 className="font-semibold text-gray-900 text-lg">Pending KYC Approvals</h2>
        <p className="text-sm text-gray-500 mt-1">Review vendor documents and approve or reject.</p>
      </div>
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-gray-400" />
      ) : pendingKyc.length === 0 ? (
        <div className="text-center py-16 text-gray-400 bg-white border rounded-xl" data-testid="no-pending-kyc">
          <FileCheck className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No vendors awaiting KYC review</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingKyc.map((v) => (
            <div key={v.id} className="bg-white border rounded-xl p-4" data-testid={`kyc-vendor-${v.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-gray-900">{v.business_name}</p>
                  <p className="text-xs text-gray-500">{v.name} · {v.email} · {v.phone || "no phone"}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Categories: {(v.service_categories || []).join(", ")}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => approveKyc(v.id)}
                    disabled={kycReviewingId === v.id}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 flex items-center gap-1 disabled:opacity-50"
                    data-testid={`approve-kyc-${v.id}`}
                  >
                    {kycReviewingId === v.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    Approve
                  </button>
                  <button
                    onClick={() => rejectKyc(v.id)}
                    disabled={kycReviewingId === v.id}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 flex items-center gap-1 disabled:opacity-50"
                    data-testid={`reject-kyc-${v.id}`}
                  >
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(v.kyc_documents || []).map((d) => (
                  <a
                    key={d.id}
                    href={`${API}/files/${d.storage_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 hover:border-[#2D2766] text-xs text-gray-700"
                    data-testid={`view-doc-${d.id}`}
                  >
                    <Eye className="w-3.5 h-3.5 shrink-0 text-[#2D2766]" />
                    <span className="truncate">{d.type.toUpperCase()}</span>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CommissionsTab({ commissionRates, setCommissionRates, loading, savingCommission, saveCommission }) {
  return (
    <div className="space-y-4" data-testid="admin-commissions">
      <div>
        <h2 className="font-semibold text-gray-900 text-lg">Commission Settings</h2>
        <p className="text-sm text-gray-500 mt-1">
          Set the platform fee (%) per vendor category. Rate is snapshotted when a vendor accepts a lead.
        </p>
      </div>
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-gray-400" />
      ) : (
        <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
          {Object.entries(commissionRates).map(([cat, rate]) => (
            <div key={cat} className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0" data-testid={`commission-row-${cat}`}>
              <p className="font-medium text-sm text-gray-800 capitalize">{cat.replace(/_/g, " ")}</p>
              <div className="relative w-32">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={rate}
                  onChange={(e) => setCommissionRates((prev) => ({ ...prev, [cat]: e.target.value }))}
                  className="w-full pl-3 pr-8 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D2766]"
                  data-testid={`commission-input-${cat}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
              </div>
            </div>
          ))}
          <button
            onClick={saveCommission}
            disabled={savingCommission}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white font-medium text-sm disabled:opacity-50"
            style={{ backgroundColor: "#2D2766" }}
            data-testid="save-commissions-btn"
          >
            {savingCommission ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Commission Rates
          </button>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [sosAlerts, setSosAlerts] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [commissionRates, setCommissionRates] = useState({});
  const [savingCommission, setSavingCommission] = useState(false);
  const [pendingKyc, setPendingKyc] = useState([]);
  const [kycReviewingId, setKycReviewingId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [newType, setNewType] = useState("announcement");
  const [loading, setLoading] = useState(true);
  const [sendingSwiggyNudge, setSendingSwiggyNudge] = useState(false);

  // Auth rides on the httpOnly admin cookie (axios withCredentials).
  const headers = useMemo(() => ({}), []);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers });
      setStats(res.data);
    } catch { toast.error("Failed to load stats"); }
  }, [headers]);

  const loadTab = useCallback(async (t) => {
    setLoading(true);
    try {
      if (t === "vendors") {
        const res = await axios.get(`${API}/admin/vendors?limit=50`, { headers });
        setVendors(res.data.vendors);
      } else if (t === "users") {
        const res = await axios.get(`${API}/admin/users?limit=100`, { headers });
        setUsers(res.data.users);
      } else if (t === "sos") {
        const res = await axios.get(`${API}/admin/sos-alerts?limit=50`, { headers });
        setSosAlerts(res.data.alerts);
      } else if (t === "announcements") {
        const res = await axios.get(`${API}/admin/announcements`, { headers });
        setAnnouncements(res.data.announcements);
      } else if (t === "commissions") {
        const res = await axios.get(`${API}/admin/commission-settings`, { headers });
        setCommissionRates(res.data.rates || {});
      } else if (t === "kyc") {
        const res = await axios.get(`${API}/admin/kyc/pending`, { headers });
        setPendingKyc(res.data.vendors || []);
      }
    } catch { toast.error("Failed to load data"); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const postAnnouncement = async () => {
    if (!newTitle.trim() || !newMessage.trim()) return;
    try {
      await axios.post(`${API}/admin/announcements`, { title: newTitle, message: newMessage, type: newType }, { headers });
      toast.success("Announcement posted");
      setNewTitle(""); setNewMessage(""); setNewType("announcement");
      loadTab("announcements");
    } catch { toast.error("Failed to post announcement"); }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, { headers });
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const sendSwiggyNudge = async () => {
    setSendingSwiggyNudge(true);
    try {
      const res = await axios.post(`${API}/admin/whatsapp/swiggy-nudge-broadcast`, {}, { headers });
      const { sent, already_connected, failed, total_candidates } = res.data;
      toast.success(
        `Swiggy nudge sent: ${sent} sent, ${already_connected} already linked, ${failed} failed (of ${total_candidates} candidates)`
      );
    } catch {
      toast.error("Failed to send Swiggy nudge broadcast");
    } finally {
      setSendingSwiggyNudge(false);
    }
  };

  const saveCommission = async () => {
    setSavingCommission(true);
    try {
      await axios.put(`${API}/admin/commission-settings`, { rates: commissionRates }, { headers });
      toast.success("Commission rates updated");
    } catch {
      toast.error("Failed to save commissions");
    } finally {
      setSavingCommission(false);
    }
  };

  const approveKyc = async (vendorId) => {
    setKycReviewingId(vendorId);
    try {
      await axios.post(`${API}/admin/kyc/${vendorId}/approve`, {}, { headers });
      toast.success("KYC approved");
      setPendingKyc((prev) => prev.filter((v) => v.id !== vendorId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally {
      setKycReviewingId(null);
    }
  };

  const rejectKyc = async (vendorId) => {
    const reason = window.prompt("Reason for rejection:");
    if (!reason) return;
    setKycReviewingId(vendorId);
    try {
      const form = new FormData();
      form.append("reason", reason);
      await axios.post(`${API}/admin/kyc/${vendorId}/reject`, form, { headers });
      toast.success("KYC rejected");
      setPendingKyc((prev) => prev.filter((v) => v.id !== vendorId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rejection failed");
    } finally {
      setKycReviewingId(null);
    }
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: Server },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "providers", label: "Providers", icon: Users },
    { id: "vendors", label: "Vendors", icon: Users },
    { id: "users", label: "Users", icon: Users },
    { id: "sos", label: "SOS Alerts", icon: AlertTriangle },
    { id: "kyc", label: "KYC Approvals", icon: FileCheck },
    { id: "announcements", label: "Announcements", icon: Megaphone },
    { id: "commissions", label: "Commissions", icon: Percent },
  ];

  const svcLabel = (t) => ({ medicine: "Medicine", cab: "Cab", nurse: "Nurse", doctor: "Doctor", food: "Food", grocery: "Grocery", dineout: "Dineout", ride: "Ride" }[t] || t);
  const statusColor = (s) => ({ pending: "bg-yellow-100 text-yellow-800", accepted: "bg-blue-100 text-blue-800", in_progress: "bg-indigo-100 text-indigo-800", completed: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800" }[s] || "bg-gray-100 text-gray-800");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F3FA" }} data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 py-3 md:px-8 shadow-sm" style={{ backgroundColor: "#2D2766" }}>
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-white" />
          <h1 className="text-white font-semibold text-lg">Elderra Admin</h1>
        </div>
        <button onClick={async () => { try { await axios.post(`${API}/admin/logout`); } catch (_) {} window.location.reload(); }}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar tabs */}
        <nav className="md:w-56 bg-white border-b md:border-b-0 md:border-r md:min-h-[calc(100vh-56px)] shrink-0">
          <div className="flex md:flex-col overflow-x-auto md:overflow-x-visible">
            {tabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                    tab === t.id ? "bg-[#EDE8F8] text-[#2D2766] border-b-2 md:border-b-0 md:border-l-3 border-[#2D2766]" : "text-gray-600 hover:bg-gray-50"
                  }`} data-testid={`admin-tab-${t.id}`}>
                  <Icon className="w-4 h-4 shrink-0" /> {t.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 p-4 md:p-6 max-w-5xl">
          {tab === "overview" && <OverviewTab stats={stats} svcLabel={svcLabel} statusColor={statusColor} />}

          {tab === "orders" && <AdminOrdersBoard />}
          {tab === "providers" && <AdminProviders />}

          {tab === "vendors" && <VendorsTab vendors={vendors} loading={loading} />}

          {tab === "users" && <UsersTab users={users} loading={loading} onSearch={async (q) => {
            setLoading(true);
            try {
              const res = await axios.get(`${API}/admin/users?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`, { headers });
              setUsers(res.data.users);
            } catch { toast.error("Search failed"); }
            setLoading(false);
          }} />}

          {tab === "sos" && <SosTab sosAlerts={sosAlerts} loading={loading} />}

          {tab === "announcements" && (
            <AnnouncementsTab
              announcements={announcements} loading={loading}
              newTitle={newTitle} setNewTitle={setNewTitle}
              newMessage={newMessage} setNewMessage={setNewMessage}
              newType={newType} setNewType={setNewType}
              postAnnouncement={postAnnouncement} deleteAnnouncement={deleteAnnouncement}
              sendSwiggyNudge={sendSwiggyNudge} sendingSwiggyNudge={sendingSwiggyNudge}
            />
          )}

          {tab === "kyc" && (
            <KycTab
              pendingKyc={pendingKyc} loading={loading} kycReviewingId={kycReviewingId}
              approveKyc={approveKyc} rejectKyc={rejectKyc}
            />
          )}

          {tab === "commissions" && (
            <CommissionsTab
              commissionRates={commissionRates} setCommissionRates={setCommissionRates}
              loading={loading} savingCommission={savingCommission} saveCommission={saveCommission}
            />
          )}
        </main>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  // The admin token is httpOnly, so the browser can't read it — ask the server
  // whether this session is still valid instead.
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/me`)
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F5F3FA" }}>
        <p className="text-gray-500" data-testid="admin-session-checking">Checking your session…</p>
      </div>
    );
  }
  return authed ? <Dashboard /> : <LoginForm onLogin={() => setAuthed(true)} />;
}
